import { useMemo } from 'react'

/** Render inline markdown (bold only) as React elements */
function InlineMarkdown({ text }: { text: string }) {
  const parts = useMemo(() => {
    const result: Array<{ bold: boolean; text: string }> = []
    let remaining = text
    while (remaining) {
      const idx = remaining.indexOf('**')
      if (idx === -1) { result.push({ bold: false, text: remaining }); break }
      if (idx > 0) result.push({ bold: false, text: remaining.slice(0, idx) })
      const end = remaining.indexOf('**', idx + 2)
      if (end === -1) { result.push({ bold: false, text: remaining.slice(idx) }); break }
      result.push({ bold: true, text: remaining.slice(idx + 2, end) })
      remaining = remaining.slice(end + 2)
    }
    return result
  }, [text])

  return (
    <>
      {parts.map((p, i) =>
        p.bold ? <strong key={i}>{p.text}</strong> : <span key={i}>{p.text}</span>
      )}
    </>
  )
}

interface PresentOptionsMessageProps {
  part: any
  /** Hide the question text when the parent message already contains it in a text part */
  hideQuestion?: boolean
}

function safeParse(value: unknown): any {
  if (value == null) return null
  if (typeof value === 'string') {
    try { return JSON.parse(value) } catch { return null }
  }
  return value
}

export function PresentOptionsMessage({ part, hideQuestion }: PresentOptionsMessageProps) {

  // Question + options always come from `input`; answer (when resolved)
  // comes from `output`. Older code parsed `output` into `data` and
  // wiped the question once an answer landed, which is why the chat
  // showed nothing where the picker used to be.
  const data = useMemo(() => {
    if (part.input) return part.input
    if (part.toolInvocation) {
      return safeParse(part.toolInvocation.args) ?? part.toolInvocation.args ?? null
    }
    return null
  }, [part]) as {
    question: string
    explanation?: string | null
    options: string[]
    optionsMeta?: Array<{ effect: string; recommended?: boolean; hint?: string | null; logoUrl?: string | null }> | null
    allowOther: boolean
    scope?: string
    recommendedIndex?: number | null
    multiSelect?: boolean
    placeholder?: string
    questionId?: string | null
    blueprintInstanceIds?: string[]
    sectionKey?: string | null
  } | null

  const answer = useMemo(() => {
    const out =
      part.state === 'output-available'
        ? safeParse(part.output)
        : safeParse(part.toolInvocation?.result)
    if (!out || typeof out !== 'object') return null
    return out as {
      status?: 'answered' | 'dismissed'
      selected?: string | string[] | null
      freeform?: boolean
    }
  }, [part])

  const answerBubble = useMemo<
    { kind: 'answered' | 'dismissed'; text: string } | null
  >(() => {
    if (!answer) return null
    if (answer.status === 'dismissed') {
      // User closed the picker with the X button. Surface the action as
      // a right-aligned bubble so the chat reads truthfully ("I skipped
      // that question") instead of leaving the prompt orphan.
      return { kind: 'dismissed', text: 'Dismissed' }
    }
    const sel = answer.selected
    if (sel == null) return null
    const text = Array.isArray(sel) ? sel.filter(Boolean).join(', ') : sel
    if (!text.trim()) return null
    return { kind: 'answered', text }
  }, [answer])

  const options = data?.options ?? []
  const isFreeformOnly = options.length === 0

  const {
    question = '',
    explanation,
    optionsMeta,
    recommendedIndex,
    multiSelect,
  } = (data ?? {}) as NonNullable<typeof data>

  if (!data?.options && !answerBubble) return null

  return (
    <div className="w-full">
      {question && !hideQuestion && (
        <p className="text-sm font-medium mb-1"><InlineMarkdown text={question} /></p>
      )}
      {explanation && !hideQuestion && (
        <p className="text-[12px] leading-[18px] text-text-subtle">{explanation}</p>
      )}
      {hideQuestion && !isFreeformOnly && options.length > 0 && (
        <p className="mt-0.5 text-[11px] text-text-placeholder">
          {options.length} option{options.length === 1 ? '' : 's'}
          {multiSelect ? ' (multi-select)' : ''}
          {recommendedIndex != null && optionsMeta?.[recommendedIndex]
            ? ' · recommended: see chip below'
            : ''}
        </p>
      )}
      {answerBubble && (
        <div className="mt-2 flex justify-end">
          <div
            className={
              answerBubble.kind === 'dismissed'
                ? 'text-muted-foreground max-w-[80%] rounded-[18px_4px_18px_18px] border border-dashed border-input bg-transparent px-4 py-2 text-sm italic'
                : 'bg-muted text-foreground max-w-[80%] rounded-[18px_4px_18px_18px] px-4 py-3 text-sm'
            }
          >
            <InlineMarkdown text={answerBubble.text} />
          </div>
        </div>
      )}
    </div>
  )
}
